import { SxProps, Theme } from '@mui/material';
import { commonStyles } from '~/theme/styles/commonStyles';
import { TDropZoneProps } from './types';

type TStyles = {
  modalContainer: SxProps<Theme>;
  importModalContainer: SxProps<Theme>;
  card: SxProps<Theme>;
  label: SxProps<Theme>;
  chip: SxProps<Theme>;
  chipContainer: SxProps<Theme>;
  dropZoneContainer: (props: TDropZoneProps) => (theme: Theme) => SxProps<Theme>;
  cardInfoContainer: SxProps<Theme>;
  menuItem: SxProps<Theme>;
  menuItemText: SxProps<Theme>;
  errorInfoMessage: SxProps<Theme>;
  collapseContainer: SxProps<Theme>;
  dragIcon: SxProps<Theme>;
  addOwnerModalPaper: SxProps<Theme>;
  addOwnerItem: SxProps<Theme>;
  diagramPaper: SxProps<Theme>;
  container: SxProps<Theme>;
  cardDiagram: SxProps<Theme>;
  cardHeader: SxProps<Theme>;
  cardIcon: SxProps<Theme>;
  textCard: SxProps<Theme>;
  sourceURL: SxProps<Theme>;
  importCardContainer: SxProps<Theme>;
  importCard: SxProps<Theme>;
  importWarningContainer: SxProps<Theme>;
  cardActionsContainer: SxProps<Theme>;
  forbidenCountriesHeader: SxProps<Theme>;
  isAbsendTitle: SxProps<Theme>;
  nameContainer: SxProps<Theme>;
  nameTextContainer: SxProps<Theme>;
  wrapText: SxProps<Theme>;
};

export const styles: TStyles = {
  modalContainer: (theme: Theme) => ({
    ...(commonStyles.scroll(theme) as Object),
    zIndex: 2,
    padding: theme.spacing(3),
    width: '80%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'center',
    background: theme.palette.common.white,
    maxHeight: '80%',
    flexWrap: 'nowrap',
    overflow: 'auto',
    outline: 'none',
  }),
  importModalContainer: () => ({
    zIndex: 2,
    maxWidth: { md: '60%', sm: '90%' },
    overflow: 'auto',
    maxHeight: '90vh',
  }),
  isAbsendTitle: { alignItems: 'center', display: 'flex', gap: 1, lineHeight: 2 },
  card: (theme: Theme) => ({
    borderRadius: 1,
    paddingX: { xs: 0.2, sm: 0.5, md: 1 },
    paddingY: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
    border: `1px solid ${theme.palette.grey[100]}`,
    minHeight: 42,
    bgcolor: theme.palette.background.default,
  }),
  cardActionsContainer: { display: 'flex', gap: 1, flexShrink: 0, justifySelf: 'center' },
  cardInfoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
  },
  label: {
    borderRadius: 1,
    border: 1,
  },
  chip: (theme: Theme) => ({
    minWidth: 'fit-content',
    '&:hover': {
      backgroundColor: theme.palette.primary[100],
    },
  }),
  chipContainer: { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', flex: 1, gap: 1 },
  dropZoneContainer:
    ({ isRoot, isOver, isDisabled }: TDropZoneProps) =>
    (theme: Theme) => ({
      background: theme.palette.background.default,
      padding: isRoot ? 1 : 0,
      border: isRoot ? `1px solid ${theme.palette.grey.additionalGrey}` : 'none',
      borderRadius: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      transition: 'background 0.15s',
      position: 'relative',
      ...(isOver &&
        !isDisabled && {
          bgcolor: 'primary.50',
          outline: '2px dashed',
          outlineColor: 'primary.main',
          outlineOffset: 4,
        }),
      ...(isDisabled && {
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundColor: theme.palette.common.black + '1e',
          borderRadius: 'inherit',
          zIndex: 1,
          pointerEvents: 'none',
          cursor: 'no-drop',
        },
      }),
    }),
  menuItem: {
    padding: 2,
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  menuItemText: {
    flexGrow: 1,
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  },
  errorInfoMessage: (theme: Theme) => ({
    border: `1px solid ${theme.palette.error.main}`,
    background: '#f7ecec',
    p: 1,
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  }),
  collapseContainer: {
    '& .MuiCollapse-wrapperInner': {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
  },
  dragIcon: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'grab',
    color: 'text.disabled',
    touchAction: 'none',
    flexShrink: 0,
    '&:hover': { color: 'text.secondary' },
    '&:active': { cursor: 'grabbing' },
  },
  nameContainer: {
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    minWidth: 0,
  },
  nameTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  wrapText: {
    overflowWrap: 'anywhere',
  },
  addOwnerModalPaper: (theme: Theme) => ({
    ...(commonStyles.scroll(theme) as Object),
    minWidth: '300px',
    maxHeight: '90vh',
    overflowY: 'auto',
    maxWidth: '30%',
    background: 'white',
  }),
  addOwnerItem: { cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, p: 1.5 },

  diagramPaper: (theme: Theme) => ({
    ...(commonStyles.scroll(theme) as Object),
    background: theme.palette.common.white,
    margin: theme.spacing(10),
    width: '85%',
    height: '85%',
    backgroundColor: theme.palette.common.white,
    padding: 4,
    display: 'flex',
    overflowY: 'scroll',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(4),
      gap: theme.spacing(1),
    },
  }),
  importWarningContainer: (theme: Theme) => ({
    border: `1px solid ${theme.palette.grey[100]}`,
    borderRadius: 0.5,
    p: 1,
    background: theme.palette.grey.disabled,
    display: 'flex',
    gap: 1,
  }),
  importCard: {
    display: 'flex',
    gap: 1,
    justifyContent: 'space-between',
    border: '1px solid gray',
    width: '100%',
    p: 2,
    borderRadius: 0.5,
    boxSizing: 'border-box',
    alignItems: 'center',
    flexDirection: { sm: 'column', md: 'row' },
  },

  importCardContainer: (theme: Theme) => ({
    ...(commonStyles.scroll(theme) as Object),
    width: '100%',
    display: 'flex',
    gap: 2,
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'auto',
    maxHeight: '70%',
  }),

  container: {
    width: '100%',
    height: '90%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
  },

  cardDiagram: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey.additionalGrey}`,
    borderRadius: 1,
    padding: '10px 14px',
    boxShadow: `0 2px 6px ${theme.palette.border.subtle}`,
    fontSize: 13,
    lineHeight: 1.4,
    width: '240px',
    minWidth: 'unset',
    maxWidth: 'unset',
    height: '100%',
    boxSizing: 'border-box',
    color: theme.palette.common.black,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  }),

  cardHeader: (theme: Theme) => ({
    fontSize: 16,
    fontWeight: '600',
    color: theme.palette.primary.main,
    marginBottom: 1,
    borderBottom: `1px dashed ${theme.palette.primary.dark}`,
    paddingBottom: 1,
  }),

  cardIcon: () => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),

  textCard: () => ({
    fontSize: 13,
    textAlign: 'center',
  }),

  sourceURL: () => ({
    fontSize: 13,
    textAlign: 'center',
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
    display: 'inline',
  }),
  forbidenCountriesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    p: 2,
    bgcolor: 'background.default',
  },
};
